import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let attempts = 0;
    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        setPaymentData(res.data);
        if (res.data.payment_status === 'paid') { setStatus('success'); return; }
        if (res.data.status === 'expired') { setStatus('expired'); return; }
        attempts++;
        if (attempts < 5) setTimeout(pollStatus, 2000);
        else setStatus('timeout');
      } catch { setStatus('error'); }
    };
    pollStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA' }}>
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-36 text-center" data-testid="payment-success-page">
        {status === 'checking' && (
          <div className="animate-fade-in">
            <Loader2 className="w-10 h-10 text-[#8A8A9A] animate-spin mx-auto mb-6" />
            <h1 className="font-display text-2xl font-light text-[#1A1A2E] mb-2">Processing</h1>
            <p className="text-sm text-[#8A8A9A]">Verifying payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-up">
            <CheckCircle2 className="w-10 h-10 text-[#FF6B4A] mx-auto mb-6" />
            <h1 className="font-display text-2xl font-light text-[#1A1A2E] mb-2" data-testid="payment-success-title">Payment confirmed.</h1>
            <p className="text-sm text-[#8A8A9A] mb-2">Credits added to your account.</p>
            {paymentData && (
              <p className="font-mono text-base text-[#1A1A2E] mb-10">
                ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard">
                <Button className="bg-[#FF6B4A] text-white font-medium rounded-md px-6 text-xs" data-testid="go-to-dashboard-btn">
                  Dashboard <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
              <Link to="/dashboard/submit">
                <Button variant="outline" className="border-white/[0.08] text-[#1A1A2E] rounded-md px-6 text-xs hover:bg-white/[0.03]" data-testid="start-rendering-btn">
                  Submit Job
                </Button>
              </Link>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="animate-fade-up">
            <XCircle className="w-10 h-10 text-[#EF4444] mx-auto mb-6" />
            <h1 className="font-display text-2xl font-light text-[#1A1A2E] mb-2" data-testid="payment-failed-title">
              {status === 'expired' ? 'Session expired.' : 'Something went wrong.'}
            </h1>
            <p className="text-sm text-[#8A8A9A] mb-8">
              {status === 'timeout' ? 'Check your email for confirmation.' : 'Please try again.'}
            </p>
            <Link to="/pricing">
              <Button className="bg-[#EF4444] text-white font-medium rounded-md px-6 text-xs" data-testid="retry-payment-btn">Try Again</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
